import { useEffect, useEffectEvent, useRef } from "react";
import type { RefObject } from "react";

export const useDragging = ({
  ref,
  active,
}: {
  ref: RefObject<HTMLElement | null>;
  active: boolean;
}) => {
  const isDragging = useRef(false);
  const hasDragged = useRef(false);
  const pointerStartPosition = useRef({ x: 0, y: 0 });
  const startOffset = useRef({ left: 0, bottom: 0 });
  const rAFRef = useRef<number | null>(null);

  const onPointerDown = useEffectEvent((e: PointerEvent) => {
    if (!ref?.current) return;

    isDragging.current = true;

    const rect = ref.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;

    pointerStartPosition.current = { x: e.clientX, y: e.clientY };
    startOffset.current = {
      left: rect.left,
      bottom: viewportHeight - rect.bottom,
    };

    // Capture the pointer to continue receiving events even if it moves outside the element
    ref.current.setPointerCapture(e.pointerId);
  });

  const onPointerMove = useEffectEvent((e: PointerEvent) => {
    if (!isDragging.current || !ref?.current || rAFRef.current !== null) return;

    rAFRef.current = requestAnimationFrame(() => {
      rAFRef.current = null;

      if (!ref.current) return;

      const deltaX = e.clientX - pointerStartPosition.current.x;
      const deltaY = e.clientY - pointerStartPosition.current.y;
      const newLeft = startOffset.current.left + deltaX;
      const newBottom = startOffset.current.bottom - deltaY;

      ref.current.style.left = `${newLeft}px`;
      ref.current.style.bottom = `${newBottom}px`;

      if (Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5) {
        hasDragged.current = true;
      }
    });
  });

  const onPointerUp = useEffectEvent((e: PointerEvent) => {
    isDragging.current = false;

    if (rAFRef.current !== null) {
      cancelAnimationFrame(rAFRef.current);
      rAFRef.current = null;
    }

    ref.current?.releasePointerCapture(e.pointerId);

    /**
     * Reset hasDragged after a short delay to allow click events to be
     * distinguished from drag events.
     */
    setTimeout(() => {
      hasDragged.current = false;
    }, 250);
  });

  useEffect(() => {
    const el = ref?.current;

    if (!el || !active) return;

    el.style.touchAction = "none";

    el.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);

    return () => {
      el.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
    };
  }, [active, ref]);

  return hasDragged;
};
